import { PerformanceDetailChart } from '@components/performanceDetailChart';
import { PerformanceOverviewChart } from '@components/performanceOverviewChart';
import { PerformanceOverviewCurrencySelection } from '@components/performanceOverviewCurrencySelection';
import { PerformanceOverviewInfo } from '@components/performanceOverviewInfo';
import { PerformanceOverviewMenu } from '@components/performanceOverviewMenu';
import { PerformanceOverviewNumbersMobile } from '@components/performanceOverviewNumbers';
import { LoadingPagePerformanceChart } from '@page/loadingPagePerformanceChart';
import { EllipsisVerticalIcon } from 'lucide-react';
import { memo } from 'react';
import { useRefetchableFragment } from 'react-relay';
import InfopanelIcon from '../../../static/front/assets/performance-chart/infopanel.svg';
import { isEqual, omit } from '../../utils/helpers';
import { performanceOverviewFragment } from './PerformanceOverview.relay';
import { changeClassNameForChartHighlighting, timeFormatter } from './helpers';
import { usePerformanceData, usePerformanceHandlers } from './hooks';
import type { PerformanceOverviewProps } from './types';

const PerformanceOverviewInner: React.FC<PerformanceOverviewProps> = (props) => {
  const { path, reportCurrency, location, currentLanguage, margins } = props;

  const [data, refetch] = useRefetchableFragment(performanceOverviewFragment, props.component);

  const {
    preparedData,
    parsedData,
    preparedDetailData,
    xDomain,
    detailXDomain,
    yDomain,
    detailYDomain,
    dataLoaded,
    performanceInfo,
    title,
    xRange,
    hasBenchmark,
    fetchFailure,
    width,
    height,
    resizeRef,
    setDataLoaded,
    setFetchFailure,
    setPreparedDetailData,
    setDetailXDomain,
    setDetailYDomain,
    setTitle,
    setPerformanceInfo,
  } = usePerformanceData({ data, refetch, reportCurrency, currentLanguage, margins, dataHeader: data.header });

  const {
    menuExpanded,
    currencySelectionVisible,
    highlightLine,
    chartIsWide,
    toggleMenu,
    showCurrencySelection,
    hideCurrencySelection,
    hoverValue,
    leaveValue,
    changeCurrency,
    updateDetailDataHandler,
    toggleChartWidth,
  } = usePerformanceHandlers({
    detailXDomain,
    parsedData,
    xRange,
    hasBenchmark,
    reportCurrency,
    dataHeader: data.header,
    currentLanguage,
    refetch,
    setDataLoaded,
    setFetchFailure,
    setPreparedDetailData,
    setDetailXDomain,
    setDetailYDomain,
    setTitle,
    setPerformanceInfo,
  });

  // Render error
  if (fetchFailure) {
    const [lang] = currentLanguage.split('-');
    const errorMessages: Record<string, string> = {
      de: 'Leider ist ein Fehler aufgetreten. Bitte laden Sie die Seite erneut.',
      en: 'An unknown error has occured. Please reload the page.',
    };

    return (
      <div className="Widget Widget-Error flex h-full w-full items-center justify-center bg-[#fafafa] text-[#a4a4a4]" style={{ height }}>
        {errorMessages[lang] ? errorMessages[lang] : errorMessages.en}
      </div>
    );
  }

  const language = location?.state?.language || 'de';
  const { chart, info, menu, disclaimer, staticDisclaimer } = data;

  let currencySelectBox = null;
  if (menu?.currencySelectBox) {
    currencySelectBox = menu.currencySelectBox;
  }

  const splittedPath = path.split('/');
  const isin = splittedPath[splittedPath.length - 1];
  const highLightingClass = changeClassNameForChartHighlighting(highlightLine);

  return (
    <div className="Widget PerformanceOverview w-full overflow-hidden mb-16">
      <div className="flex items-center justify-between w-full border-b border-black font-heading text-base tablet:text-2xl text-black">
        <span>{title}</span>
        <div className="flex items-center gap-1">
          <button type="button" className="hover:cursor-pointer h-6 w-6 hidden desktop:block" onClick={toggleChartWidth}>
            <InfopanelIcon />
          </button>
          <EllipsisVerticalIcon className="h-5 w-5 hover:cursor-pointer hover:text-accent" onClick={toggleMenu} />
        </div>
      </div>

      <div className="Base-disclaimer disclaimer pt-5">{staticDisclaimer}</div>
      <div className="overviewChartWrapperUpper relative">
        {dataLoaded ? (
          <div>
            <div className={`overviewChartWrapperTest relative float-left mt-2.5 inline-block w-full desktop:w-[70%] ${chartIsWide ? 'desktop:w-full' : ''}`}>
              <div className="performanceOverviewChartGroup relative h-[320px] tablet:h-[370px] w-full" ref={resizeRef}>
                <div
                  className={`performanceDetailChart ${highLightingClass} ${reportCurrency} h-[87%] w-full [&_g_text]:font-[Arial] [&_g_text]:text-sm [&_g_text]:leading-[22px] [&_g_text]:text-black [&_.xGridLine]:stroke-black [&_.xGridLine]:stroke-0! [&_.yGridLinePlus]:stroke-black [&_.yGridLinePlus]:stroke-[1px]! [&_.yGridLineMinus]:stroke-black [&_.yGridLineMinus]:stroke-[1px]! [&_.y-grid-axis_g_.zero-gridline]:[stroke-dasharray:1,2,1]! [&_.xAxis_path]:stroke-0! [&_.xAxisTickLabel]:fill-black! [&_.xAxisTicks]:stroke-black! [&_.yAxis_path]:stroke-black! [&_.yAxis_path]:stroke-0! [&_.yAxisTickLabel]:fill-black [&_.yAxisTicks]:stroke-black! [&_path]:transition-all [&_path]:duration-500 [&_path]:ease-in-out [&_path]:[stroke-linejoin:round] [&_path.performance]:stroke-accent [&_path.benchmark]:stroke-black [&.performanceDetailChart-highlightPerformance_path.performance]:stroke-[3px] [&.performanceDetailChart-highlightBenchmark_path.benchmark]:stroke-[3px] [&.performanceDetailChart-highlightPerformance_path.benchmark]:opacity-25 [&.performanceDetailChart-highlightBenchmark_path.performance]:opacity-25`}
                >
                  <PerformanceDetailChart
                    data={preparedDetailData}
                    language={language}
                    hasBenchmarkData={hasBenchmark}
                    width={width}
                    height={height * 0.87}
                    margins={{ top: 15, right: 3, bottom: 35, left: 35 }}
                    xDomain={detailXDomain}
                    yDomain={detailYDomain}
                    xRange={xRange}
                    timeFormatter={timeFormatter.bind(null, currentLanguage)}
                  />
                </div>
                <PerformanceOverviewChart
                  chart={chart as any}
                  data={preparedData}
                  width={width}
                  height={height * 0.13}
                  margins={margins}
                  xDomain={xDomain}
                  yDomain={yDomain}
                  detailXDomain={detailXDomain || null}
                  xRange={xRange}
                  highlightLine={highlightLine}
                  location={location}
                  reportcurrency={reportCurrency}
                  updateDetailData={updateDetailDataHandler}
                  currentLanguage={currentLanguage}
                />
              </div>
              <PerformanceOverviewCurrencySelection
                selected={reportCurrency}
                isVisible={currencySelectionVisible}
                currencySelection={currencySelectBox}
                onSave={changeCurrency}
                onCancel={hideCurrencySelection}
                currencySelectionInfo={menu.currencySelectionInfo}
                currencySelectionSaveButtonText={menu.currencySelectionSaveButtonText}
                currencySelectionHeader={menu.currencySelectionHeader}
              />
            </div>
            <div
              className={`infoWrapper absolute right-0 float-left inline-block h-[380px] w-[29%] overflow-hidden border-l border-[#f9f9f9] bg-[rgba(255,255,255,0.9)] ml-[1%] transition-transform duration-500 ease-in-out ${chartIsWide ? 'translate-x-full' : 'translate-x-0'} max-desktop:top-8 max-desktop:-right-full max-desktop:w-[45%] max-desktop:overflow-visible max-desktop:border-0 max-desktop:transition-[right] max-desktop:duration-500 ${menuExpanded ? 'sub-is-visible max-desktop:right-0' : ''}`}
            >
              {info ? (
                <PerformanceOverviewInfo
                  data={info}
                  language={language}
                  onMouseEnter={hoverValue}
                  highlightLine={highlightLine}
                  onMouseLeave={leaveValue}
                  reportCurrency={reportCurrency}
                  currentperformance={performanceInfo.performance}
                  paperformance={performanceInfo.performancePA}
                  currentbenchmark={performanceInfo.benchmark}
                  pabenchmark={performanceInfo.benchmarkPA}
                  toggleChartWidth={toggleChartWidth}
                />
              ) : null}
            </div>
            <PerformanceOverviewMenu
              menu={menu}
              disclaimer={disclaimer}
              reportCurrency={reportCurrency}
              openCurrencySelection={showCurrencySelection}
              preparedData={parsedData}
              detailXDomain={detailXDomain}
              isVisible={menuExpanded}
              isin={isin}
              language={language}
              hasBenchmark={hasBenchmark}
            />
          </div>
        ) : (
          <div className={`overviewChartWrapperTest relative float-left mt-2.5 inline-block w-full desktop:w-[70%] ${chartIsWide ? 'desktop:w-full' : ''}`} ref={resizeRef}>
            <LoadingPagePerformanceChart fakeExtension={true} />
          </div>
        )}
        {!dataLoaded ? <LoadingPagePerformanceChart /> : null}
        {dataLoaded && info ? (
          <PerformanceOverviewNumbersMobile
            data={info as any}
            reportCurrency={reportCurrency}
            currentperformance={performanceInfo.performance}
            currentbenchmark={performanceInfo.benchmark}
            paperformance={performanceInfo.performancePA}
            pabenchmark={performanceInfo.benchmarkPA}
            onMouseEnter={hoverValue}
            onMouseLeave={leaveValue}
            language={language}
          />
        ) : null}
        <div className="Base-disclaimer disclaimer pt-5">{disclaimer}</div>
      </div>
    </div>
  );
};

// Wrap with React.memo using custom comparator that omits `component` from shallow equality check
export const PerformanceOverview = memo(PerformanceOverviewInner, (prevProps, nextProps) => {
  const reducedNextProps = omit(nextProps, ['component']);
  const reducedPrevProps = omit(prevProps, ['component']);
  return isEqual(reducedPrevProps, reducedNextProps);
});
